import {
  Body1Strong,
  Body1Stronger,
  Caption1,
  Card,
  CardFooter,
  CardHeader,
  Divider,
  Button,
} from '@fluentui/react-components';
import { Bookmark16Filled, Bookmark16Regular } from '@fluentui/react-icons';
import React from 'react';
import { Link } from 'react-router-dom';
import { scrollToTop } from '../Shared/Shared';
import { useRoleCardStyles } from './RoleCard.styles';
import { useDispatch, useSelector } from 'react-redux';
import { addBookmark, careerStateSelector, loadBookmarks, removeBookmark } from '@careerhub/common';
import { CareerHubModuleReactContext } from '@careerhub/common';
import { FeatureName, SubFeatureName, EventName } from '../resources/TelemetryEventDetails';
import { IRoleCardProps } from './RoleCardV2.types';
import { Announced } from 'office-ui-fabric-react';

const DESCRIPTION_MAX_LENGTH = 250;

export function RoleCard(props: IRoleCardProps) {
  const styles = useRoleCardStyles();
  const [bookmarked, setBookmarked] = React.useState(false);
  const [announceText, setAnnounceText] = React.useState('');
  const { bookmarks, taxonomyTypes } = useSelector(careerStateSelector);
  const { roleData, homePageStyle } = props;
  const dispatch = useDispatch();
  const { telemetryClient } = React.useContext(CareerHubModuleReactContext);
  const bookmarkRef = React.useRef<HTMLButtonElement>(null);
  const bookmarkActionPendingRef = React.useRef(false);
  const previousBookmarkedRef = React.useRef<boolean | null>(null);

  const truncatedDescription = React.useMemo(() => {
    if (!roleData.description) return '';
    if (roleData.description.length <= DESCRIPTION_MAX_LENGTH) return roleData.description;
    return roleData.description.substring(0, DESCRIPTION_MAX_LENGTH).trimEnd() + '…';
  }, [roleData.description]);

  const onBookmarkClick = React.useCallback(() => {
    dispatch(bookmarked ? removeBookmark(roleData.id!) : addBookmark(roleData.id!));
    telemetryClient.logPinUnpinChange(
      homePageStyle ? FeatureName.CareerPath : FeatureName.DisciplinePage,
      SubFeatureName.Bookmark,
      bookmarked,
      { roleId: roleData.id }
    );
    bookmarkActionPendingRef.current = true;
  }, [dispatch, bookmarked, roleData.id, telemetryClient, homePageStyle]);

  React.useEffect(() => {
    dispatch(loadBookmarks());
  }, [dispatch]);

  React.useEffect(() => {
    if (!announceText) return;

    const announcementTimer = window.setTimeout(() => setAnnounceText(''), 1000);
    return () => window.clearTimeout(announcementTimer);
  }, [announceText]);

  React.useEffect(() => {
    const isBookmarked = bookmarks.some((bookmark) => bookmark.id === roleData.id);
    setBookmarked(isBookmarked);
    if (previousBookmarkedRef.current !== null && previousBookmarkedRef.current !== isBookmarked) {
      setAnnounceText(isBookmarked ? `Bookmarked for ${roleData.name}` : `Bookmark removed for ${roleData.name}`);
      if (bookmarkActionPendingRef.current) {
        bookmarkRef.current?.focus();
        bookmarkActionPendingRef.current = false;
      }
    }
    previousBookmarkedRef.current = isBookmarked;
  }, [bookmarks, roleData.id, roleData.name]);

  const onViewDetailsClick: any = () => {
    scrollToTop();
    telemetryClient.logButtonClick(
      homePageStyle ? FeatureName.CareerPath : FeatureName.DisciplinePage,
      SubFeatureName.ViewCareerStageDetail,
      {
        roleId: roleData.id,
        eventName: EventName.NavigateToRolePage,
      }
    );
  };
  const level3Type = taxonomyTypes.find((item) => item.level === 3)?.id || 'Role';
  return (
    <Card className={homePageStyle ? styles.homepageCard : styles.card}>
      <CardHeader
        className={styles.header}
        header={
          <Body1Stronger as="h3" className={styles.title}>
            {roleData.name}
          </Body1Stronger>
        }
        // add current role indicator after card title overflow is handled
        description={
          <Body1Strong as="h4" className={styles.subtitle}>
            Role summary
          </Body1Strong>
        }
      />
      <Caption1 className={styles.caption}>{truncatedDescription}</Caption1>
      <Divider className={styles.divider} />
      <CardFooter className={styles.footer}>
        <Link
          className={styles.link}
          to={`/${level3Type}/${roleData?.id}`}
          onClick={onViewDetailsClick}
          aria-label={`view details of ${roleData.name}`}
        >
          View details
        </Link>
        <Button
          className={styles.buttonColor}
          icon={bookmarked ? <Bookmark16Filled aria-hidden="true" /> : <Bookmark16Regular aria-hidden="true" />}
          aria-label={`${bookmarked ? 'Filled' : 'Unfilled'} Bookmark for ${roleData.name}`}
          aria-pressed={bookmarked}
          appearance="transparent"
          onClick={onBookmarkClick}
          ref={bookmarkRef}
        />
        <Announced message={announceText} aria-live="assertive" className={styles.srOnly} />
      </CardFooter>
    </Card>
  );
}
